import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Eye, EyeSlash, LockKey, WarningCircle, EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { useAuth, formatApiError } from "../context/AuthContext";
import { useT } from "../context/LangContext";
import { CONTACT } from "../lib/site";

const inputCls = "w-full rounded-full border border-border bg-bone px-5 py-3.5 text-ink outline-none transition-colors duration-200 focus:border-terracotta disabled:opacity-60";
const primaryCls = "group flex w-full items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 font-semibold text-bone transition-colors duration-200 hover:bg-terracotta disabled:opacity-60";

const PasswordField = ({ id, value, onChange, placeholder, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input id={id} data-testid={testId} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete={id === "login-password" ? "current-password" : "new-password"} className={`${inputCls} pr-12`} />
      <button type="button" onClick={() => setShow((s) => !s)} aria-label="toggle" className="absolute right-4 top-1/2 -translate-y-1/2 text-stone hover:text-ink">
        {show ? <EyeSlash size={20} /> : <Eye size={20} />}
      </button>
    </div>
  );
};

export const LoginForm = ({ c }) => {
  const { checkEmail, requestVerification, verifyToken, login, setPassword } = useAuth();
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState(() => (params.get("verify") ? "verifying" : "email")); // email | login | verify | sent | verifying | create | denied
  const [email, setEmail] = useState("");
  const [name, setName] = useState(null);
  const [token, setToken] = useState(() => params.get("verify") || "");
  const [password, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail, t.login.genericError));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!token || step !== "verifying") return;
    (async () => {
      try {
        const r = await verifyToken(token);
        setEmail(r.email);
        setName(r.name);
        setStep("create");
      } catch (e) {
        setError(formatApiError(e?.response?.data?.detail, t.login.genericError));
        setStep("email");
      } finally {
        setParams({}, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEmail = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    run(async () => {
      const r = await checkEmail(email);
      setName(r.name);
      if (!r.allowed) setStep("denied");
      else setStep(r.has_password ? "login" : "verify");
    });
  };

  const onSendLink = () => run(async () => {
    await requestVerification(email);
    setStep("sent");
  });

  const onLogin = (e) => {
    e.preventDefault();
    run(() => login(email, password));
  };

  const onCreate = (e) => {
    e.preventDefault();
    if (password.length < 8) return setError(c.pwTooShort);
    if (password !== confirm) return setError(c.pwMismatch);
    run(() => setPassword(token, password));
  };

  const back = () => {
    setStep("email");
    setPw("");
    setConfirm("");
    setError("");
    setToken("");
  };

  const errorBox = error && (
    <div data-testid="login-error" className="flex items-start gap-3 rounded-2xl border border-terracotta/40 bg-terracotta/5 p-4 text-sm text-terracotta-dark">
      <WarningCircle size={22} weight="duotone" className="mt-0.5 shrink-0 text-terracotta" />
      <span>{error}</span>
    </div>
  );

  if (step === "denied") {
    return (
      <div className="space-y-5" data-testid="login-denied">
        <div className="flex items-start gap-3 rounded-2xl border border-terracotta/40 bg-terracotta/5 p-4 text-sm text-terracotta-dark">
          <WarningCircle size={22} weight="duotone" className="mt-0.5 shrink-0 text-terracotta" />
          <span>{c.unauthorized} <a href={`mailto:${CONTACT.email}`} className="font-semibold underline">{CONTACT.email}</a></span>
        </div>
        <button type="button" onClick={back} data-testid="login-back-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-terracotta"><ArrowLeft size={16} /> {c.back}</button>
      </div>
    );
  }

  if (step === "verifying") {
    return (
      <div className="flex items-center gap-3 text-sm text-stone" data-testid="login-verifying">
        <div className="h-5 w-5 rounded-full border-2 border-terracotta border-t-transparent animate-spin" /> {c.verifying}
      </div>
    );
  }

  if (step === "email") {
    return (
      <form onSubmit={onEmail} className="space-y-4" data-testid="login-email-step">
        <label htmlFor="login-email" className="block text-sm font-semibold text-ink">{c.emailLabel}</label>
        <input id="login-email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={c.emailPlaceholder} autoComplete="email" disabled={busy} className={inputCls} />
        {errorBox}
        <button type="submit" disabled={busy} data-testid="login-continue-btn" className={primaryCls}>
          {c.continueBtn} <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
        </button>
      </form>
    );
  }

  const greeting = name ? `${c.hello} ${name.split(" ")[0]}` : email;

  if (step === "verify") {
    return (
      <div className="space-y-4" data-testid="login-verify-step">
        <div className="rounded-2xl bg-forest/10 px-4 py-3 text-sm text-ink"><span className="font-semibold">{greeting}</span> · {c.verifyIntro}</div>
        <p className="text-sm text-stone leading-relaxed">{c.verifyBody} <span className="font-semibold text-ink">{email}</span></p>
        {errorBox}
        <button type="button" onClick={onSendLink} disabled={busy} data-testid="send-verification-btn" className={primaryCls}>
          <PaperPlaneTilt size={20} weight="bold" /> {c.sendLinkBtn}
        </button>
        <button type="button" onClick={back} data-testid="login-back-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-ink hover:text-terracotta"><ArrowLeft size={14} /> {c.back}</button>
      </div>
    );
  }

  if (step === "sent") {
    return (
      <div className="space-y-4" data-testid="login-sent-step">
        <div className="flex items-start gap-3 rounded-2xl border border-forest/30 bg-forest/5 p-4 text-sm text-ink">
          <EnvelopeSimple size={22} weight="duotone" className="mt-0.5 shrink-0 text-forest" />
          <span><span className="font-semibold">{c.sentTitle}</span> {c.sentBody} <span className="font-semibold">{email}</span>. {c.sentHint}</span>
        </div>
        <button type="button" onClick={back} data-testid="login-back-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-ink hover:text-terracotta"><ArrowLeft size={14} /> {c.back}</button>
      </div>
    );
  }

  if (step === "login") {
    return (
      <form onSubmit={onLogin} className="space-y-4" data-testid="login-password-step">
        <div className="rounded-2xl bg-sand px-4 py-3 text-sm text-ink"><span className="font-semibold">{greeting}</span> · {email}</div>
        <label htmlFor="login-password" className="block text-sm font-semibold text-ink">{c.passwordLabel}</label>
        <PasswordField id="login-password" testId="login-password-input" value={password} onChange={setPw} placeholder="••••••••" />
        {errorBox}
        <button type="submit" disabled={busy || !password} data-testid="login-submit-btn" className={primaryCls}>
          <LockKey size={20} weight="bold" /> {c.loginBtn}
        </button>
        <div className="flex items-center justify-between text-sm">
          <button type="button" onClick={back} data-testid="login-back-btn" className="inline-flex items-center gap-1 font-semibold text-ink hover:text-terracotta"><ArrowLeft size={14} /> {c.back}</button>
          <a href={`mailto:${CONTACT.email}?subject=Reset%20password%20area%20soci`} className="text-stone hover:text-terracotta">{c.forgot}</a>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={onCreate} className="space-y-4" data-testid="login-create-step">
      <div className="rounded-2xl bg-forest/10 px-4 py-3 text-sm text-ink">
        <span className="font-semibold">{greeting}</span> · {c.createIntro}
      </div>
      <label htmlFor="create-password" className="block text-sm font-semibold text-ink">{c.newPasswordLabel}</label>
      <PasswordField id="create-password" testId="create-password-input" value={password} onChange={setPw} placeholder={c.pwHint} />
      <label htmlFor="confirm-password" className="block text-sm font-semibold text-ink">{c.confirmPasswordLabel}</label>
      <PasswordField id="confirm-password" testId="confirm-password-input" value={confirm} onChange={setConfirm} placeholder="••••••••" />
      {errorBox}
      <button type="submit" disabled={busy || !password || !confirm} data-testid="create-password-btn" className={primaryCls}>
        <LockKey size={20} weight="bold" /> {c.createBtn}
      </button>
      <button type="button" onClick={back} data-testid="login-back-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-ink hover:text-terracotta"><ArrowLeft size={14} /> {c.back}</button>
    </form>
  );
};
